import { useEffect, useRef } from 'react';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import { getCashFundCoverage } from '@shared/utils/cashFund/cashFundCoverage';
import { useAccommodationsConsoleTelemetry } from '../AccommodationsConsole.telemetry';

type UseTrackCashFundSplitImpressionArgs = Readonly<{
  /** The listings actually rendered, after filtering and sorting. */
  listings: ReadonlyArray<AccommodationListingDTO>;
  /** Set only by the cash-fund variant; nothing is reported without it. */
  cashFund?: CashFundConfig;
}>;

/**
 * Reports whether the couple saw a part-paid stay or only fully covered ones — once per console.
 *
 * Waits for a real split instead of firing with the results, because the splits land with the
 * listings and the results event fires before them. A search with no eligible stay reports
 * nothing: the couple saw no fund treatment, so there is no impression to record.
 */
export const useTrackCashFundSplitImpression = ({ listings, cashFund }: UseTrackCashFundSplitImpressionArgs): void => {
  const { cashFundSplitViewed } = useAccommodationsConsoleTelemetry();
  const hasReportedRef = useRef(false);

  useEffect(() => {
    if (!cashFund || hasReportedRef.current) return;

    // One pass: a listing with no split contributes nothing, so no intermediate list of holes.
    const outOfPocketAmounts = listings.flatMap(listing => getCashFundCoverage(listing, cashFund)?.outOfPocketMinorUnits ?? []);
    if (outOfPocketAmounts.length === 0) return;

    hasReportedRef.current = true;
    cashFundSplitViewed({ cash_fund_view: outOfPocketAmounts.some(amount => amount > 0) ? 'eligible_partial' : 'eligible' });
  }, [listings, cashFund, cashFundSplitViewed]);
};
